import { computed, onBeforeUnmount, ref } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import DebtConversationApi from 'dashboard/api/debtConversation';

/**
 * Resumo de conversa com IA (B1) de UMA conversa.
 *
 * O que este composable NÃO faz, de propósito: nada é gerado sozinho. A
 * chamada gasta token de LLM e por isso só sai atrás de um clique explícito.
 *
 * O 503 do backend tem dois significados e eles NÃO se resolvem do mesmo
 * jeito: com `Retry-After` é indisponibilidade temporária (o botão fica
 * desabilitado pelo intervalo, em vez de deixar o agente repetir na hora);
 * sem o cabeçalho é o recurso desligado no DEBTHUB, e repetir não ajuda.
 *
 * @param {Function} getConversationDisplayId devolve o display_id da conversa.
 * @returns {Object} estado e ações do resumo.
 */
export function useConversationSummary(getConversationDisplayId) {
  const { accountId } = useAccount();
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  // A flag nasce off; sem ela o endpoint responde 404 e a UI não aparece.
  const isFeatureEnabled = computed(() => {
    if (!accountId.value) return false;
    return isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.DEBT_CONVERSATION_SUMMARY
    );
  });

  const summary = ref(null);
  const isLoading = ref(false);
  const errorKey = ref(null);
  const retryAfterSeconds = ref(0);
  let retryTimer = null;

  const stopRetryCountdown = () => {
    clearInterval(retryTimer);
    retryTimer = null;
  };

  const startRetryCountdown = seconds => {
    stopRetryCountdown();
    retryAfterSeconds.value = seconds;
    retryTimer = setInterval(() => {
      retryAfterSeconds.value -= 1;
      if (retryAfterSeconds.value <= 0) stopRetryCountdown();
    }, 1000);
  };

  onBeforeUnmount(stopRetryCountdown);

  // `parcial` é a única coisa que separa "resumo com todas as citações
  // verificadas" de "resumo que PERDEU apoio e foi podado". As duas telas são
  // diferentes, e apresentar o podado como verificado seria mentir.
  const isPartial = computed(() => summary.value?.parcial === true);
  const citations = computed(() => summary.value?.citacoes ?? []);
  const verification = computed(() => summary.value?.verificacao ?? null);
  const isInputTruncated = computed(
    () => summary.value?.entrada?.truncada === true
  );

  const isRetryBlocked = computed(() => retryAfterSeconds.value > 0);
  const canGenerate = computed(
    () => !isLoading.value && !isRetryBlocked.value && isFeatureEnabled.value
  );

  // Retry-After chega em SEGUNDOS (RETRY_AFTER_SECONDS no controller). Valor
  // não numérico (um HTTP-date, que este backend não emite) não vira bloqueio:
  // um NaN travaria o botão para sempre.
  const retryAfterFromError = error => {
    const raw = error?.response?.headers?.['retry-after'];
    const seconds = Number(raw);
    return Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  };

  // 404 é indistinguível por decisão do backend: flag desligada e conversa
  // negada respondem a mesma coisa, para a existência do recurso não vazar. A
  // mensagem da tela não pode afirmar qual dos dois é.
  const applyError = error => {
    const httpStatus = error?.response?.status;
    if (httpStatus === 404) {
      errorKey.value = 'NOT_AVAILABLE';
      return;
    }
    if (httpStatus === 422) {
      errorKey.value = 'EMPTY_THREAD';
      return;
    }
    // Guarda de concorrência do backend (K8): resumo já em voo para esta
    // conta/usuário, ou para esta mesma conversa. O botão fica utilizável de
    // novo assim que a chamada anterior liberar a vaga — sem countdown, ao
    // contrário do 503 com Retry-After, porque a liberação não tem prazo
    // fixo para o front prever.
    if (httpStatus === 429) {
      errorKey.value = 'RATE_LIMITED';
      return;
    }
    if (httpStatus === 503) {
      const seconds = retryAfterFromError(error);
      if (seconds > 0) {
        errorKey.value = 'TEMPORARILY_UNAVAILABLE';
        startRetryCountdown(seconds);
        return;
      }
      errorKey.value = 'DISABLED';
      return;
    }
    if (httpStatus === 502) {
      errorKey.value = 'FAILED';
      return;
    }
    errorKey.value = 'GENERIC';
  };

  // Carrega o resumo JA GUARDADO. Silencioso de propósito: não achar nada é o
  // caso comum (conversa nunca resumida), não um erro para mostrar ao agente —
  // a tela simplesmente oferece o botão de gerar.
  const isRestoring = ref(false);
  const load = async () => {
    const displayId = getConversationDisplayId();
    if (!displayId || !isFeatureEnabled.value || summary.value) return;
    isRestoring.value = true;
    try {
      const { data } = await DebtConversationApi.storedSummary(displayId);
      if (data && typeof data.resumo === 'string') summary.value = data;
    } catch {
      // Sem resumo guardado, ou leitura indisponível: o botão de gerar continua.
    } finally {
      isRestoring.value = false;
    }
  };

  // Quando o resumo veio do armazenamento, dizer QUANDO e por QUEM — e quantas
  // mensagens entraram depois. Um resumo de ontem apresentado como atual é pior
  // que nenhum resumo.
  const generatedAt = computed(() => summary.value?.gerado_em ?? null);
  const generatedBy = computed(() => summary.value?.gerado_por ?? null);
  const newMessagesSince = computed(
    () => summary.value?.mensagens_novas ?? null
  );

  const generate = async () => {
    const displayId = getConversationDisplayId();
    if (!displayId || !canGenerate.value) return false;

    isLoading.value = true;
    errorKey.value = null;
    try {
      const { data } = await DebtConversationApi.summary(displayId);
      summary.value = data;
      return true;
    } catch (error) {
      applyError(error);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    load,
    isRestoring,
    generatedAt,
    generatedBy,
    newMessagesSince,
    isFeatureEnabled,
    summary,
    isLoading,
    errorKey,
    isPartial,
    citations,
    verification,
    isInputTruncated,
    retryAfterSeconds,
    isRetryBlocked,
    canGenerate,
    generate,
  };
}
